import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Card from "../components/ui-components/Card";
import Badge from "../components/ui-components/Badge";
import VerificationReport from "./VerificationReport";
import {
	getTenant,
	updateTenant,
	setTenantStatus,
	provisionTenant,
	getProvisionStatus,
	verifyTenant,
	provisionTenantDataAccess,
	provisionTenantIdentity,
	getIdentityStatus,
	verifyTenantIdentity,
	seedTenant,
	buildTenantDataset,
	getDatasetStatus,
	getTenantReadiness,
} from "./platformApi";
import { deriveNames, GCP_PROJECT } from "./deriveNames";

// The same table as internal/tenant/lifecycle.go. Duplicating it here shapes
// the UI; the server enforces it. If the two ever disagree the server wins
// and the console shows its 409.
const TRANSITIONS = {
	provisioning: ["active", "archived"],
	active: ["suspended", "archived"],
	suspended: ["active", "archived"],
	archived: [],
};

// Same two maps as the list screen: Badge's `status` vocabulary does not
// contain the lifecycle values, and its `label` is rendered verbatim.
const STATUS_TONE = {
	provisioning: "pending",
	active: "success",
	suspended: "warning",
	archived: "cancelled",
};

const statusLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const ACTION_LABEL = { active: "Activate", suspended: "Suspend", archived: "Archive" };

// Returns a copy: callers sort it, and sorting the table in place would
// silently reorder the buttons for every later render.
export const allowedTransitions = (status) => (TRANSITIONS[status] || []).slice();
export const reasonRequired = (to) => to !== "active";

// A transition OUT of active takes a tenant off the air immediately, so it
// also demands the tenant id be typed. This is an affordance, not a
// guarantee: the guarantees are the transition table and the reason, both
// enforced server-side.
const typedConfirmationRequired = (from) => from === "active";

const RESOURCE_FIELDS = [
	{ name: "bqDataset", label: "BigQuery dataset" },
	{ name: "gcsBucket", label: "GCS bucket" },
	{ name: "bqServiceAccount", label: "Service account" },
	{ name: "bqConnection", label: "BigQuery connection" },
];

// The keys the edit form owns. Derived from RESOURCE_FIELDS so adding a
// resource cannot be half-done: a field that renders but is missing here
// would show the operator an input whose changes are silently dropped.
const EDITABLE = ["displayName", ...RESOURCE_FIELDS.map((f) => f.name)];

// Width is inline: theme.width is overridden with a tiny custom scale, so
// w-full is a no-op in this codebase.
const FIELD_WIDTH = { width: "100%" };

// How often the console asks Cloud Run what the execution is doing. The job
// takes minutes, not seconds; a tighter interval would only add requests.
export const POLL_MS = 5000;

// Provisioning is offered only while the tenant is still provisioning.
// Re-running it against a live tenant would re-apply an IAM policy under
// a tenant that is currently serving traffic, and the apply is only
// idempotent with respect to state the job itself put there.
export const canProvision = (status) => status === "provisioning";

// Data access, unlike provisioning, is offered for a live tenant. It has to
// be: every tenant that exists today is active and has no connection, and
// this is the step that gives them one. It is also how a compromised
// database password is rotated, which cannot require taking the tenant down.
// Only archived refuses -- its resources are gone, so handing it a live
// database role would resurrect access to them.
export const canProvisionDataAccess = (status) => status !== "archived" && Boolean(status);

// Identity, like provisioning and unlike data access, is offered only while
// the tenant is still provisioning. The job rewrites the realm's clients
// create-or-update, so re-running it against a live tenant would repoint the
// redirect URIs of a realm people are currently logged into. The server
// refuses this too, with a 409; this predicate only keeps the button from
// being offered.
export const canProvisionIdentity = (status) => status === "provisioning";

// Seeding is provisioning-only, and the server says so too (409 otherwise): it
// is create-or-update over schedules a live tenant may since have edited.
//
// The dataset build is NOT. A tenant now activates with an empty dataset and
// connects its cloud billing export afterwards, so the first real build always
// happens on an active tenant -- provisioning-only would leave the button
// unreachable in the only state anyone needs it. Suspended and archived stay
// out: those are not states to start a long job in, and the server agrees.
export const canSeed = (status) => status === "provisioning";
export const canBuildDataset = (status) => status === "provisioning" || status === "active";

// Activation is offered once readiness has been RUN and nothing is BLOCKING.
// Not "not known to fail": an operator who has never asked cannot be assumed to
// have a green answer, and a null report is exactly the case where the console
// would otherwise be enabling the one irreversible-in-practice step on no
// evidence.
//
// `ok` is the server's own Blocking() answer, not "every row is green" --
// readiness carries advisory rows (the tenant has no billing data yet) that are
// reported and do not gate. Counting rows here instead would re-impose the gate
// the server just dropped. The server refuses independently; this only keeps
// the button from promising something that will 409.
export const canActivate = (readiness) => Boolean(readiness && readiness.ok);

const RUN_LABEL = {
	running: "Provisioning…",
	succeeded: "Provisioning finished",
	failed: "Provisioning failed",
};

// The dataset build is a dbt run, not a provisioning run, and saying
// "Provisioning finished" over a finished build would read as the resources
// having been created a second time.
const BUILD_LABEL = {
	running: "Building…",
	succeeded: "Build finished",
	failed: "Build failed",
};

// One place that turns a record into form state, so the load and the
// data-access refresh cannot drift apart.
const formFrom = (r) =>
	Object.fromEntries(EDITABLE.map((k) => [k, r[k] || ""]));

export default function TenantDetailPage() {
	const { id } = useParams();

	const [record, setRecord] = useState(null);
	const [form, setForm] = useState(null);
	const [error, setError] = useState(null);
	const [saving, setSaving] = useState(false);

	// Provisioning state. `execution` is the Cloud Run execution name handed
	// back by the launch; M3a stores no column for it, so it lives here for
	// as long as the operator stays on the page.
	const [execution, setExecution] = useState(null);
	const [run, setRun] = useState(null);
	const [provisionError, setProvisionError] = useState(null);
	const [launching, setLaunching] = useState(false);
	const [report, setReport] = useState(null);
	const [verifying, setVerifying] = useState(false);
	const [verifyError, setVerifyError] = useState(null);

	// Data access is synchronous -- no execution to poll -- so it needs only
	// a busy flag, an error and a note that it has run.
	const [dataAccessBusy, setDataAccessBusy] = useState(false);
	const [dataAccessError, setDataAccessError] = useState(null);
	const [dataAccessDone, setDataAccessDone] = useState(false);

	// Identity has its own execution, its own report and its own errors --
	// deliberately not shared with provisioning above. The two planes fail
	// independently and an operator has to be able to see that a realm is
	// broken while the dataset is fine.
	const [identityExecution, setIdentityExecution] = useState(null);
	const [identityRun, setIdentityRun] = useState(null);
	const [identityError, setIdentityError] = useState(null);
	const [identityLaunching, setIdentityLaunching] = useState(false);
	const [identityReport, setIdentityReport] = useState(null);
	const [identityVerifying, setIdentityVerifying] = useState(false);
	const [identityVerifyError, setIdentityVerifyError] = useState(null);

	// The first user of the new realm. Both optional: a realm may be
	// provisioned before anyone is invited to it. The password is component
	// state and nothing else -- it is posted once, the API writes it to Secret
	// Manager, the job consumes it and destroys the version.
	const [firstUserEmail, setFirstUserEmail] = useState("");
	const [temporaryPassword, setTemporaryPassword] = useState("");

	// Seeding is synchronous, like data access: a busy flag, an error, and the
	// report of what it wrote.
	const [seedBusy, setSeedBusy] = useState(false);
	const [seedError, setSeedError] = useState(null);
	const [seedReport, setSeedReport] = useState(null);

	// The dataset build is a job, so it polls like provisioning and identity --
	// and again keeps its own execution and errors. A dbt run failing says
	// nothing about whether the realm is correct.
	const [buildExecution, setBuildExecution] = useState(null);
	const [buildRun, setBuildRun] = useState(null);
	const [buildError, setBuildError] = useState(null);
	const [buildLaunching, setBuildLaunching] = useState(false);

	// Readiness is the gate. Null means "nobody has asked", which is NOT the
	// same as "not ready" for display purposes but is treated the same by the
	// Activate button: see canActivate.
	const [readiness, setReadiness] = useState(null);
	const [readinessBusy, setReadinessBusy] = useState(false);
	const [readinessError, setReadinessError] = useState(null);

	const [pendingTarget, setPendingTarget] = useState(null);
	const [reason, setReason] = useState("");
	const [typed, setTyped] = useState("");

	useEffect(() => {
		let cancelled = false;
		getTenant(id)
			.then((r) => {
				if (cancelled) return;
				setRecord(r);
				setForm(formFrom(r));
			})
			.catch((e) => !cancelled && setError(e.message));
		return () => {
			cancelled = true;
		};
	}, [id]);

	const runVerify = useCallback(async () => {
		setVerifyError(null);
		setVerifying(true);
		try {
			// A failing report is a 200, so this resolves and the failures
			// render. Only a transport or authorization fault throws.
			setReport(await verifyTenant(id));
		} catch (e) {
			setVerifyError(e);
		} finally {
			setVerifying(false);
		}
	}, [id]);

	// Poll while an execution is running, and verify once when it stops.
	// Verification runs whether the job succeeded or failed: "succeeded" is
	// the job's own account of the calls it believes it made, and the point
	// of the report is to check that against GCP.
	const runDone = run ? run.done : false;

	useEffect(() => {
		if (!execution || runDone) return undefined;

		let cancelled = false;
		const tick = async () => {
			try {
				const next = await getProvisionStatus(id, execution);
				if (cancelled) return;
				setRun(next);
				if (next.done) runVerify();
			} catch (e) {
				if (!cancelled) setProvisionError(e.message);
			}
		};

		tick();
		const timer = setInterval(tick, POLL_MS);
		return () => {
			cancelled = true;
			clearInterval(timer);
		};
	}, [id, execution, runDone, runVerify]);

	const onProvision = async () => {
		setProvisionError(null);
		setReport(null);
		setRun(null);
		setLaunching(true);
		try {
			const { execution: name } = await provisionTenant(id);
			setExecution(name);
		} catch (e) {
			setProvisionError(e.message);
		} finally {
			setLaunching(false);
		}
	};

	// The response carries the tenant record with bqConnection set. The
	// generated database password is never in it -- it exists only long enough
	// to reach the connection's stored credential.
	const onProvisionDataAccess = async () => {
		setDataAccessError(null);
		setDataAccessBusy(true);
		try {
			const updated = await provisionTenantDataAccess(id);
			setRecord(updated);
			setForm(formFrom(updated));
			setDataAccessDone(true);
			// Verify immediately: two of the eleven checks are the connection
			// and its binding, which this action is the only thing that sets.
			runVerify();
		} catch (e) {
			setDataAccessError(e.message);
		} finally {
			setDataAccessBusy(false);
		}
	};

	const runIdentityVerify = useCallback(async () => {
		setIdentityVerifyError(null);
		setIdentityVerifying(true);
		try {
			// Expect the DNS check to fail on a fresh tenant: instantview.ai is
			// delegated to a registrar GCP cannot write to, so the CNAME is a
			// manual step and this report is what says it is still owed.
			setIdentityReport(await verifyTenantIdentity(id));
		} catch (e) {
			setIdentityVerifyError(e);
		} finally {
			setIdentityVerifying(false);
		}
	}, [id]);

	const identityRunDone = identityRun ? identityRun.done : false;

	useEffect(() => {
		if (!identityExecution || identityRunDone) return undefined;

		let cancelled = false;
		const tick = async () => {
			try {
				const next = await getIdentityStatus(id, identityExecution);
				if (cancelled) return;
				setIdentityRun(next);
				// Verify on failure too: "succeeded" is the job's account of the
				// calls it believes it made, and half a realm is the state worth
				// seeing itemised.
				if (next.done) runIdentityVerify();
			} catch (e) {
				if (!cancelled) setIdentityError(e.message);
			}
		};

		tick();
		const timer = setInterval(tick, POLL_MS);
		return () => {
			cancelled = true;
			clearInterval(timer);
		};
	}, [id, identityExecution, identityRunDone, runIdentityVerify]);

	const onProvisionIdentity = async () => {
		setIdentityError(null);
		setIdentityReport(null);
		setIdentityRun(null);
		setIdentityLaunching(true);
		try {
			const { execution: name } = await provisionTenantIdentity(id, {
				firstUserEmail: firstUserEmail.trim(),
				temporaryPassword,
			});
			setIdentityExecution(name);
			// Cleared on launch, not on success: from here the password lives in
			// Secret Manager and there is no reason for a copy to sit in a form
			// field on an operator's screen while the job runs.
			setTemporaryPassword("");
		} catch (e) {
			setIdentityError(e.message);
		} finally {
			setIdentityLaunching(false);
		}
	};

	const runReadiness = useCallback(async () => {
		setReadinessError(null);
		setReadinessBusy(true);
		try {
			// A red report is a 200 -- "six of nine pass" is the answer, not a
			// failure to answer. Only a transport or authorization fault throws.
			setReadiness(await getTenantReadiness(id));
		} catch (e) {
			setReadinessError(e);
		} finally {
			setReadinessBusy(false);
		}
	}, [id]);

	const onSeed = async () => {
		setSeedError(null);
		setSeedBusy(true);
		try {
			setSeedReport(await seedTenant(id));
			// Three of the nine checks are the ones this just wrote, so the
			// operator sees the effect without having to ask for it.
			runReadiness();
		} catch (e) {
			setSeedError(e.message);
		} finally {
			setSeedBusy(false);
		}
	};

	const buildRunDone = buildRun ? buildRun.done : false;

	useEffect(() => {
		if (!buildExecution || buildRunDone) return undefined;

		let cancelled = false;
		const tick = async () => {
			try {
				const next = await getDatasetStatus(id, buildExecution);
				if (cancelled) return;
				setBuildRun(next);
				// Re-check on failure too: a build that failed halfway still
				// leaves tables behind, and "which of the nine are green now"
				// is the question that decides what to do next.
				if (next.done) runReadiness();
			} catch (e) {
				if (!cancelled) setBuildError(e.message);
			}
		};

		tick();
		const timer = setInterval(tick, POLL_MS);
		return () => {
			cancelled = true;
			clearInterval(timer);
		};
	}, [id, buildExecution, buildRunDone, runReadiness]);

	const onBuildDataset = async () => {
		setBuildError(null);
		setBuildRun(null);
		setBuildLaunching(true);
		try {
			const { execution: name } = await buildTenantDataset(id);
			setBuildExecution(name);
		} catch (e) {
			setBuildError(e.message);
		} finally {
			setBuildLaunching(false);
		}
	};

	const resourcesLocked = record ? record.status !== "provisioning" : true;

	// Display only. The server derives this itself and checks it against GCP;
	// this is here so the operator can see WHICH account is about to be granted
	// before clicking, not to decide anything.
	const grantTarget = record
		? record.bqServiceAccount || deriveNames(record.id, GCP_PROJECT).serviceAccount
		: "";

	const changed = useMemo(() => {
		if (!record || !form) return {};
		const out = {};
		EDITABLE.forEach((k) => {
			if (form[k] !== (record[k] || "")) out[k] = form[k];
		});
		return out;
	}, [record, form]);

	const onSave = async () => {
		setError(null);
		setSaving(true);
		try {
			setRecord(await updateTenant(id, changed));
		} catch (e) {
			setError(e.message);
		} finally {
			setSaving(false);
		}
	};

	// The readiness gate applies to ONE transition: provisioning -> active.
	// Suspended -> active is deliberately ungated, here and on the server. A
	// tenant is suspended for reasons that have nothing to do with its
	// plumbing, and an operator restoring service during an incident must not
	// be blocked by nine live GCP round-trips.
	const activationGated = record?.status === "provisioning" && pendingTarget === "active";

	const confirmDisabled =
		!pendingTarget ||
		(reasonRequired(pendingTarget) && reason.trim() === "") ||
		(typedConfirmationRequired(record?.status) && typed !== id) ||
		(activationGated && !canActivate(readiness));

	const onConfirm = async () => {
		setError(null);
		try {
			const updated = await setTenantStatus(id, { to: pendingTarget, reason: reason.trim() });
			setRecord(updated);
			setPendingTarget(null);
			setReason("");
			setTyped("");
		} catch (e) {
			// A refused activation carries the report that refused it. Showing
			// it beats the operator re-asking for a report the server just
			// computed -- and it is the authoritative one, where the card's may
			// be minutes stale.
			if (e.readiness) setReadiness(e.readiness);
			setError(e.message);
		}
	};

	if (error && !record) return <Card><p role="alert" className="text-[13px] text-danger">{error}</p></Card>;
	if (!record || !form) return <Card><p className="text-[13px] text-text-2">Loading…</p></Card>;

	return (
		<div className="flex flex-col gap-4">
			<Card>
				<div className="mb-4 flex items-center gap-3">
					<h1 className="text-[15px] font-semibold">{record.displayName}</h1>
					<Badge status={STATUS_TONE[record.status] || "neutral"} label={statusLabel(record.status)} size="sm" />
					<span className="font-mono text-[12px] text-text-3">{record.id}</span>
				</div>

				<div className="mb-4">
					<label htmlFor="displayName" className="mb-1 block text-[13px] font-medium">Display name</label>
					<input
						id="displayName"
						value={form.displayName}
						onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
						style={FIELD_WIDTH}
						className="box-border rounded-ctrl border border-solid border-card-bd bg-card px-3 py-2 text-[13px]"
					/>
				</div>

				{RESOURCE_FIELDS.map((f) => (
					<div key={f.name} className="mb-4">
						<label htmlFor={f.name} className="mb-1 block text-[13px] font-medium">{f.label}</label>
						<input
							id={f.name}
							value={form[f.name]}
							disabled={resourcesLocked}
							onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
							style={FIELD_WIDTH}
							className="box-border rounded-ctrl border border-solid border-card-bd bg-card px-3 py-2 font-mono text-[13px] disabled:bg-surface-2 disabled:text-text-3"
						/>
					</div>
				))}

				{resourcesLocked && (
					<p className="mb-4 text-[12px] text-text-3">
						Resource names are frozen once a tenant leaves provisioning. Repointing them would serve this
						tenant another dataset&apos;s rows on the next cache expiry. To correct them, archive the
						tenant and create a new record.
					</p>
				)}

				{error && <p role="alert" className="mb-3 text-[13px] text-danger">{error}</p>}

				<button
					type="button"
					onClick={onSave}
					disabled={saving || Object.keys(changed).length === 0}
					className="box-border rounded-ctrl border border-solid border-transparent bg-primary px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
				>
					{saving ? "Saving…" : "Save changes"}
				</button>
			</Card>

			{canProvision(record.status) && (
				<Card>
					<h2 className="mb-1 text-[13px] font-semibold">Provisioning</h2>
					<p className="mb-3 text-[13px] text-text-2">
						Creates the dataset, the bucket and the tenant service account, and binds them — as a Cloud
						Run job under its own identity. The API does not hold those permissions. Re-running is safe:
						every step is a create-if-absent or a set-to-this-value.
					</p>

					{run && (
						<p className="mb-3 text-[13px]">
							<strong>{RUN_LABEL[run.state] || run.state}</strong>
							{run.message ? ` — ${run.message}` : ""}
						</p>
					)}

					{provisionError && (
						<p role="alert" className="mb-3 text-[13px] text-danger">{provisionError}</p>
					)}

					<button
						type="button"
						onClick={onProvision}
						disabled={launching || (run && !run.done)}
						className="box-border rounded-ctrl border border-solid border-transparent bg-primary px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
					>
						{launching ? "Starting…" : run ? "Provision again" : "Provision resources"}
					</button>

					<p className="mt-3 text-[12px] text-text-3">
						Provisioning does not activate the tenant. Activate it yourself, after the verification below
						passes and after you have confirmed the tenant is refused where it should be.
					</p>
				</Card>
			)}

			{canProvisionDataAccess(record.status) && (
				<Card>
					<h2 className="mb-1 text-[13px] font-semibold">Data access</h2>
					<p className="mb-3 text-[13px] text-text-2">
						Creates this tenant&apos;s database role and its own BigQuery connection, then grants the
						tenant service account the right to use <em>that</em> connection only. Configuration data
						stays live; the tenant sees only its own rows, enforced by row-level security in Postgres.
						Re-running rotates the database password.
					</p>
					<p className="mb-3 text-[13px] text-text-2">
						Run this after the resources above exist — it needs the service account to grant to.
						{!record.bqServiceAccount && (
							<>
								{" "}
								This tenant has none recorded, so the grant goes to the derived account{" "}
								<span className="font-mono">{grantTarget}</span>, which the server confirms exists
								before it binds anything.
							</>
						)}
					</p>

					{record.bqConnection && (
						<p className="mb-3 text-[13px]">
							Connection: <span className="font-mono">{record.bqConnection}</span>
							{dataAccessDone ? " — provisioned" : ""}
						</p>
					)}

					{dataAccessError && (
						<p role="alert" className="mb-3 text-[13px] text-danger">{dataAccessError}</p>
					)}

					<button
						type="button"
						onClick={onProvisionDataAccess}
						disabled={dataAccessBusy}
						className="box-border rounded-ctrl border border-solid border-transparent bg-primary px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
					>
						{dataAccessBusy
							? "Provisioning…"
							: record.bqConnection
								? "Rotate data access"
								: "Provision data access"}
					</button>

					<p className="mt-3 text-[12px] text-text-3">
						The tenant does not read through this connection until its dataset is rebuilt against it.
						Rebuild first, then set the service account — that is the cutover, not this button.
					</p>
				</Card>
			)}

			{canProvisionIdentity(record.status) && (
				<Card>
					<h2 className="mb-1 text-[13px] font-semibold">Identity</h2>
					<p className="mb-3 text-[13px] text-text-2">
						Creates the Keycloak realm, its public and confidential clients, the audience mapper every
						request depends on, and the Cloud Run domain mapping for{" "}
						<span className="font-mono">{record.id}.instantview.ai</span> — as a second Cloud Run job under
						its own identity. Realm administration means being able to create a user in any realm, so the
						API launches this job and reads the result back; it never holds that credential itself.
					</p>

					<div className="mb-4">
						<label htmlFor="firstUserEmail" className="mb-1 block text-[13px] font-medium">
							First user email
						</label>
						<input
							id="firstUserEmail"
							type="email"
							value={firstUserEmail}
							onChange={(e) => setFirstUserEmail(e.target.value)}
							style={FIELD_WIDTH}
							className="box-border rounded-ctrl border border-solid border-card-bd bg-card px-3 py-2 text-[13px]"
						/>
					</div>

					<div className="mb-2">
						<label htmlFor="temporaryPassword" className="mb-1 block text-[13px] font-medium">
							Temporary password
						</label>
						<input
							id="temporaryPassword"
							type="password"
							autoComplete="new-password"
							value={temporaryPassword}
							onChange={(e) => setTemporaryPassword(e.target.value)}
							style={FIELD_WIDTH}
							className="box-border rounded-ctrl border border-solid border-card-bd bg-card px-3 py-2 text-[13px]"
						/>
					</div>

					<p className="mb-3 text-[12px] text-text-3">
						Both fields are optional, but an empty email has a consequence worth knowing before you
						submit: seeding reads the first user from the record this run leaves behind, not from
						Keycloak, so a tenant provisioned without one cannot be seeded until identity is run again
						with an email. Leave it empty only to create the realm now and invite its first user later,
						or when re-running to repair a realm that already has one.
					</p>
					<p className="mb-3 text-[12px] text-text-3">
						Re-running with an email that already exists in the realm leaves that user untouched — the
						password below is not applied to them, and the seedable record is written either way. On a
						new user the password is marked temporary and must be changed at first login, so whoever
						runs this never learns the real one. It is never written to a log or a job argument.
					</p>

					{identityRun && (
						<p className="mb-3 text-[13px]">
							<strong>{RUN_LABEL[identityRun.state] || identityRun.state}</strong>
							{identityRun.message ? ` — ${identityRun.message}` : ""}
						</p>
					)}

					{identityError && (
						<p role="alert" className="mb-3 text-[13px] text-danger">{identityError}</p>
					)}

					<button
						type="button"
						onClick={onProvisionIdentity}
						disabled={identityLaunching || (identityRun && !identityRun.done)}
						className="box-border rounded-ctrl border border-solid border-transparent bg-primary px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
					>
						{identityLaunching ? "Starting…" : identityRun ? "Provision identity again" : "Provision identity"}
					</button>

					<p className="mt-3 text-[12px] text-text-3">
						One step is not automatable: <span className="font-mono">instantview.ai</span> is delegated to
						GoDaddy, so add <span className="font-mono">{record.id} CNAME ghs.googlehosted.com.</span>{" "}
						there yourself. The certificate is not issued until that record resolves publicly — the DNS
						check below is what tells you it is still owed.
					</p>
				</Card>
			)}

			{canSeed(record.status) && (
				<Card>
					<h2 className="mb-1 text-[13px] font-semibold">Control-plane data</h2>
					<p className="mb-3 text-[13px] text-text-2">
						Writes the rows a tenant needs before any page renders: the ingestion pipeline and its
						stages, the schedules that run it, and the owner role for the first user created above.
						Synchronous and in the API, because these are the control-plane database and that is where
						its credentials already live.
					</p>
					<p className="mb-3 text-[13px] text-text-2">
						Re-running is safe — every row is created if absent and reported as existing otherwise. The
						one thing it refuses is a tenant that already has users, because a second run would mint a
						second owner.
					</p>

					{seedReport && (
						<p className="mb-3 text-[13px]">
							<strong>
								{seedReport.created} created, {seedReport.existing} already present
							</strong>
						</p>
					)}

					{seedError && <p role="alert" className="mb-3 text-[13px] text-danger">{seedError}</p>}

					<button
						type="button"
						onClick={onSeed}
						disabled={seedBusy}
						className="box-border rounded-ctrl border border-solid border-transparent bg-primary px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
					>
						{seedBusy ? "Seeding…" : seedReport ? "Seed again" : "Seed control-plane data"}
					</button>

					<p className="mt-3 text-[12px] text-text-3">
						Run this after identity — the owner grant needs the first user to exist. Without it the
						tenant logs in to a console with no pipeline, which looks like a broken product rather than
						an unfinished setup.
					</p>
				</Card>
			)}

			{canBuildDataset(record.status) && (
				<Card>
					<h2 className="mb-1 text-[13px] font-semibold">Dataset build</h2>
					<p className="mb-3 text-[13px] text-text-2">
						Runs dbt against <span className="font-mono">{record.bqDataset}</span> as a third Cloud Run
						job, building the tenant&apos;s tables and views through its own BigQuery connection.
					</p>
					<p className="mb-3 text-[13px] text-text-2">
						<strong>Connect a cloud account and let the first import run before pressing this.</strong>{" "}
						dbt transforms the <span className="font-mono">stg_gcp_*</span> tables that the import
						creates from the tenant&apos;s own billing export — it does not create them. On a tenant
						that has never imported there is nothing to transform, and this refuses rather than
						spending two minutes failing inside dbt. Activation does not wait for any of it.
					</p>
					<p className="mb-3 text-[13px] text-text-2">
						It runs snapshots as well as models. A run without them completes green and leaves{" "}
						<span className="font-mono">hst_gcp_billing_resources</span> missing, which then fails at
						request time on whichever page reads it first.
					</p>

					{buildRun && (
						<p className="mb-3 text-[13px]">
							<strong>{BUILD_LABEL[buildRun.state] || buildRun.state}</strong>
							{buildRun.message ? ` — ${buildRun.message}` : ""}
						</p>
					)}

					{buildError && <p role="alert" className="mb-3 text-[13px] text-danger">{buildError}</p>}

					<button
						type="button"
						onClick={onBuildDataset}
						disabled={buildLaunching || (buildRun && !buildRun.done)}
						className="box-border rounded-ctrl border border-solid border-transparent bg-primary px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
					>
						{buildLaunching ? "Starting…" : buildRun ? "Build again" : "Build dataset"}
					</button>

					<p className="mt-3 text-[12px] text-text-3">
						Run this after data access — the build reads configuration through the tenant&apos;s own
						connection, and a build that used the platform default would bake the wrong one in.
					</p>
				</Card>
			)}

			{/* Ungated, exactly like the data-plane report below and for the same
			    reason: verification is a READ. The provision action above is
			    gated because it rewrites the realm's clients, but "is this
			    tenant's realm correct?" is a question an operator must be able
			    to ask about a live tenant, and about one somebody else
			    provisioned in a session whose report is long gone. */}
			<VerificationReport
				report={identityReport}
				onRerun={runIdentityVerify}
				isLoading={identityVerifying}
				error={identityVerifyError}
				title="Identity verification"
				source="Keycloak, Cloud Run and public DNS"
				subject="identity"
			/>

			{/* Always rendered, never gated on a report existing. The report is
			    component state: it is lost on reload, and every path that used
			    to produce one ran off the back of an ACTION. An operator who
			    reloaded the page -- or who inherited a tenant provisioned in
			    another session -- had no way to ask "is this tenant correct?"
			    without re-running provisioning under traffic to get the answer
			    as a side effect. The empty state carries the button. */}
			<VerificationReport
				report={report}
				onRerun={runVerify}
				isLoading={verifying}
				error={verifyError}
			/>

			{/* The gate, and the last card before Lifecycle on purpose: it is the
			    question the Activate button below is the answer to. Same
			    Check/Report shape as the two verifications, so it renders in the
			    same component. The server computes it again inside activation and
			    refuses on any BLOCKING red row; rows badged "Pending" are the
			    advisory ones and hold nothing up. */}
			<VerificationReport
				report={readiness}
				onRerun={runReadiness}
				isLoading={readinessBusy}
				error={readinessError}
				title="Activation readiness"
				source="GCP, Keycloak and the control-plane database"
				subject="readiness"
			/>

			<Card>
				<h2 className="mb-3 text-[13px] font-semibold">Lifecycle</h2>
				{allowedTransitions(record.status).length === 0 ? (
					<p className="text-[13px] text-text-2">
						Archived is terminal. This tenant can no longer change status.
					</p>
				) : (
					<div className="flex gap-2">
						{allowedTransitions(record.status).map((to) => (
							<button
								key={to}
								type="button"
								onClick={() => {
									setPendingTarget(to);
									setReason("");
									setTyped("");
								}}
								className="box-border rounded-ctrl border border-solid border-card-bd px-3 py-1.5 text-[13px]"
							>
								{ACTION_LABEL[to]}
							</button>
						))}
					</div>
				)}

				{pendingTarget && (
					<div className="mt-4 border-t border-card-bd pt-4">
						<p className="mb-3 text-[13px]">
							{ACTION_LABEL[pendingTarget]} <strong>{record.displayName}</strong>?
							{typedConfirmationRequired(record.status) &&
								" This takes the tenant off the air immediately."}
						</p>

						{reasonRequired(pendingTarget) && (
							<div className="mb-3">
								<label htmlFor="reason" className="mb-1 block text-[13px] font-medium">Reason</label>
								<input
									id="reason"
									value={reason}
									onChange={(e) => setReason(e.target.value)}
									style={FIELD_WIDTH}
									className="box-border rounded-ctrl border border-solid border-card-bd bg-card px-3 py-2 text-[13px]"
								/>
							</div>
						)}

						{typedConfirmationRequired(record.status) && (
							<div className="mb-3">
								<label htmlFor="typed" className="mb-1 block text-[13px] font-medium">
									Type the tenant id to confirm
								</label>
								<input
									id="typed"
									value={typed}
									onChange={(e) => setTyped(e.target.value)}
									placeholder={record.id}
									style={FIELD_WIDTH}
									className="box-border rounded-ctrl border border-solid border-card-bd bg-card px-3 py-2 font-mono text-[13px]"
								/>
							</div>
						)}

						{activationGated && !canActivate(readiness) && (
							<p className="mb-3 text-[13px] text-text-2">
								{readiness
									? "Every readiness check above must pass before this tenant can be activated, except the ones badged Pending — those describe data the tenant has not imported yet. Activation is when its boundary starts being tested by real traffic."
									: "Run the readiness check above first. Activation is when this tenant's boundary starts being tested by real traffic, and it is not worth doing on an unread report."}
							</p>
						)}

						<div className="flex gap-2">
							<button
								type="button"
								onClick={onConfirm}
								disabled={confirmDisabled}
								className="box-border rounded-ctrl border border-solid border-transparent bg-primary px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
							>
								Confirm
							</button>
							<button
								type="button"
								onClick={() => setPendingTarget(null)}
								className="box-border rounded-ctrl border border-solid border-card-bd px-3 py-1.5 text-[13px]"
							>
								Cancel
							</button>
						</div>
					</div>
				)}
			</Card>
		</div>
	);
}
